import { useEffect, useState } from "react";
import { Star, Save } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { FALLBACK_GRADE_PRICING, GRADE_PRICING_KEY, type GradePricingValues } from "@/hooks/useGradePricing";

const GRADES = ["A", "B", "C"] as const;

/** Platform › Data › Grade Pricing Defaults (superadmin). Edits platform_settings.grade_pricing. */
export default function GradePricingDefaults() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [values, setValues] = useState<GradePricingValues>(FALLBACK_GRADE_PRICING);

  const { data: platform } = useQuery({
    queryKey: ["platform-grade-pricing"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("platform_settings")
        .select("id, setting_value")
        .eq("setting_key", GRADE_PRICING_KEY)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });
  useEffect(() => {
    const v = platform?.setting_value as any;
    if (v && GRADES.every((g) => typeof v[g] === "number")) setValues(v);
  }, [platform]);

  const { data: overrides = [] } = useQuery({
    queryKey: ["grade-pricing-overrides"],
    queryFn: async () => {
      const [{ data: rows, error }, { data: companies, error: cErr }] = await Promise.all([
        supabase.from("company_settings").select("company_id, setting_value").eq("setting_key", GRADE_PRICING_KEY),
        supabase.from("companies").select("id, name"),
      ]);
      if (error) throw error;
      if (cErr) throw cErr;
      const names = new Map((companies || []).map((c) => [c.id, c.name]));
      return (rows || [])
        .map((r) => ({ id: r.company_id, name: names.get(r.company_id) ?? r.company_id, v: r.setting_value as any }))
        .sort((a, b) => a.name.localeCompare(b.name));
    },
  });

  const valid = GRADES.every((g) => Number.isFinite(values[g]) && values[g] >= 0 && values[g] <= 100);

  const save = useMutation({
    mutationFn: async () => {
      const value = { A: values.A, B: values.B, C: values.C };
      const q = platform
        ? supabase.from("platform_settings").update({ setting_value: value, updated_at: new Date().toISOString() }).eq("setting_key", GRADE_PRICING_KEY).select("id")
        : supabase.from("platform_settings").insert({ setting_key: GRADE_PRICING_KEY, setting_value: value }).select("id");
      const { data, error } = await q;
      if (error) throw error;
      if (!data || data.length === 0) throw new Error("No rows updated");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["platform-grade-pricing"] });
      qc.invalidateQueries({ queryKey: ["platform-settings"] });
      qc.invalidateQueries({ queryKey: ["grade-pricing"] });
      toast({ title: "Grade pricing defaults saved" });
    },
    onError: (e: any) => toast({ title: "Error saving settings", description: e.message, variant: "destructive" }),
  });

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="px-6 pt-8 pb-6">
        <div className="flex items-center gap-2 mb-1">
          <Star className="w-5 h-5 text-primary" />
          <h1 className="text-2xl font-bold text-foreground">Grade Pricing Defaults</h1>
        </div>
        <p className="text-muted-foreground">Resale price per quality grade, as a % of the new retail price.</p>
      </div>
      <div className="px-6 max-w-4xl space-y-6">
        <section className="bg-card rounded-xl border border-border p-5 space-y-4">
          <div className="grid grid-cols-3 gap-4">
            {GRADES.map((g) => (
              <div key={g}>
                <label className="text-sm font-medium text-foreground mb-1 block">Grade {g} (% of retail)</label>
                <Input
                  type="number" min={0} max={100}
                  value={Number.isFinite(values[g]) ? values[g] : ""}
                  onChange={(e) => setValues({ ...values, [g]: e.target.value === "" ? NaN : Number(e.target.value) })}
                />
              </div>
            ))}
          </div>
          <p className="text-sm text-muted-foreground">Companies without their own override use these values.</p>
          <Button onClick={() => save.mutate()} disabled={!valid || save.isPending} className="gap-2">
            <Save className="w-4 h-4" /> Save
          </Button>
        </section>

        <section className="bg-card rounded-xl border border-border p-5 space-y-3">
          <h2 className="font-semibold text-foreground">Companies with their own override</h2>
          {overrides.length === 0 ? (
            <p className="text-sm text-muted-foreground">No company overrides these values.</p>
          ) : (
            <Table>
              <TableHeader><TableRow><TableHead>Company</TableHead><TableHead>A</TableHead><TableHead>B</TableHead><TableHead>C</TableHead></TableRow></TableHeader>
              <TableBody>
                {overrides.map((o) => (
                  <TableRow key={o.id}>
                    <TableCell>{o.name}</TableCell>
                    {GRADES.map((g) => <TableCell key={g}>{o.v?.[g] ?? "—"}%</TableCell>)}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </section>
      </div>
    </div>
  );
}
